import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Search } from "styled-icons/bootstrap";
import colors from "styles/colors";
import { Check2 } from "styled-icons/bootstrap";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";
import Row from "components/Row";
import Column from "components/Column";
import Icon from "components/Icon";

const FacebookPageList = ({ fbValueDisabled, fbPageList, fbPageName, fbPageId, fbPageImg, currentTab }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const rmpToken = sessionStorage.getItem("rmp_token");
  const facebookUserInfoResult = useSelector((state) => facebookSelectors.getFacebookUserInfoResult(state));
  const facebookPageList = useSelector((state) => facebookSelectors.getFacebookPageListResult(state));
  const facebookPageListMore = useSelector((state) => facebookSelectors.getFacebookPageListMoreResult(state));
  const facebookFormLoading = useSelector((state) => facebookSelectors.setFacebookFormLoadingResult(state));
  const [isOptionsVisible, setOptionsVisible] = useState(false);
  const [selectedPage, setSelectedPage] = useState({ id: null, name: null, picture: null, url: null });
  const [pageListDisplay, setPageListDisplay] = useState(fbPageList);
  const [allPageList, setAllPageList] = useState(fbPageList);
  const [searchPageList, setSearchPageList] = useState(false);
  const [limitPageDisplay, setLimitPageDisplay] = useState(100);

  const textPageRef = useRef(null);
  const [isPageOverflowing, setIsPageOverflowing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const textPageElement = textPageRef.current;

    if (textPageElement) {
      const isPageOverflowing = textPageElement.scrollWidth > textPageElement.clientWidth;
      setIsPageOverflowing(isPageOverflowing);
    }
  }, [textPageRef]);

  /**
   * Handles the click event for the "Load More" button.
   * Adds more pages to the page list display if there are more pages available.
   * @returns {Promise<void>}
   */
  const handleClickLoadMore = async () => {
    const limitNumber = 100;
    if (pageListDisplay.length < allPageList.length) {
      let limitCount = limitPageDisplay + limitNumber;
      setLimitPageDisplay(limitCount);
      const updatedPageList = [...pageListDisplay];
      allPageList.forEach((page) => {
        const exists = updatedPageList.find((item) => item.id === page.id);

        if (!exists && updatedPageList.length < limitCount) {
          updatedPageList.push(page);
        }
      });

      setPageListDisplay(updatedPageList);
    }
  };

  const LoadMoreButtonContainer = () => {
    return (
      pageListDisplay.length < allPageList.length &&
      !searchPageList && (
        <LoadMoreButton>
          <div className="load-more-text" onClick={() => handleClickLoadMore()}>
            {t("__show_more")}
          </div>
        </LoadMoreButton>
      )
    );
  };

  /**
   * Fetches the Facebook page list and updates the state with the retrieved data.
   * @returns {Promise<void>} A Promise that resolves when the Facebook page list has been fetched and updated.
   */
  const fetchFacebookPageList = async () => {
    let loadMore = facebookPageList.data.loadMore;
    const updatedPageList = [...allPageList];
    while (loadMore != null) {
      const getPageList = await dispatch(
        facebookActions.getFacebookPageListMore({
          token: rmpToken,
          userAccessToken:
            facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED"
              ? facebookUserInfoResult.data.user_access_token
              : null,
          loadMore: loadMore,
        }),
      );

      getPageList.data.pageList.forEach((page) => {
        const exists = updatedPageList.find((item) => item.id === page.id);

        if (!exists) {
          updatedPageList.push(page);
        }
      });

      setAllPageList(updatedPageList);

      loadMore = getPageList.data.loadMore;
    }
  };

  const getAllPageList = async () => {
    if (
      facebookUserInfoResult.result == 0 &&
      facebookUserInfoResult.msg === "GET_DATA_COMPLETED" &&
      facebookPageList.result == 0 &&
      facebookPageList.data.pageList != null &&
      facebookPageList.data.pageList.length > 0
    ) {
      fetchFacebookPageList();
    }
  };

  const pageList = () => {
    /**get form list by page url */
    const getFormList = async (item) => {
      await dispatch(facebookActions.setFacebookFormLoading(true));
      const pageIdResult = await dispatch(
        facebookActions.getFacebookPageId({
          token: rmpToken,
          userAccessToken:
            facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED"
              ? facebookUserInfoResult.data.user_access_token
              : null,
          pageUrl: item.link,
        }),
      );

      if (pageIdResult.result === 0 && pageIdResult.msg === "GET_DATA_COMPLETED") {
        const formList = await dispatch(
          facebookActions.getFacebookFormList({
            token: rmpToken,
            pageAccessToken: pageIdResult.data.access_token,
            pageUrl: item.link,
            appReserve: currentTab,
          }),
        );

        if (formList.result === 0) {
          await dispatch(facebookActions.setValidateFacebookLeadForm({ result: false }));
          if (formList.data.formList != null) {
            let radioValue = "all";
            let pageFormSelectType = "all";
            let checkedFormItems = [];

            if (formList.data.formStatus == "SOMEUSED" || formList.data.formStatus == "ALLUSED") {
              radioValue = "select";
              pageFormSelectType = "select";
              checkedFormItems = [];
            }
            localStorage.setItem("radioValue", radioValue);
            await dispatch(facebookActions.changePageFormSelectType(pageFormSelectType));
            await dispatch(facebookActions.setCheckedFormItems(checkedFormItems));
          } else {
            let radioValue = null;
            let pageFormSelectType = null;
            let checkedFormItems = null;
            localStorage.setItem("radioValue", radioValue);
            await dispatch(facebookActions.changePageFormSelectType(pageFormSelectType));
            await dispatch(facebookActions.setCheckedFormItems(checkedFormItems));
          }
        }
        await dispatch(facebookActions.setFacebookFormLoading(false));
      } else {
        await dispatch(facebookActions.setFacebookFormLoading(false));
      }
    };

    const handleOnSelected = (item, index) => {
      dispatch(facebookActions.setDuplicateFacebookForm({ duplicate: null }));
      setSelectedPage({ id: item.id, name: item.name, picture: item.picture, url: item.link });
      dispatch(facebookActions.changePageUrlInput(item.link));
      getFormList(item);
    };
    const onDropdownButtonClicked = () => {
      setOptionsVisible(!isOptionsVisible);
      getAllPageList();
    };

    /** action when select page */
    const onDropdownItemSelected = (item, index) => {
      onDropdownButtonClicked();
      handleOnSelected(item, index);
    };

    const DropdownItem = ({ item, index, onClick }) => {
      return (
        <StyledItem onClick={() => onClick(item, index)}>
          <ItemContent type="dropdown">
            <div className="page-data-area">
              <PageImage src={item.picture} />
              <div className="page-name-area">
                <div className="page-name-dropdown">{item.name}</div>
                <div className="page-id-dropdown">{item.id}</div>
              </div>
            </div>
            {selectedPage.name != null
              ? selectedPage.name == item.name && <CheckIcon color="#555" />
              : fbPageName == item.name && <CheckIcon color="#555" />}
          </ItemContent>
        </StyledItem>
      );
    };

    /**
     * Filters the list of Facebook pages based on the search term entered by the user.
     * The search term is matched against the name or ID of each page.
     * Updates the displayed page list with the filtered results and sets the search state.
     *
     * @function searchPage
     * @returns {void}
     */
    const searchPage = () => {
      const page = inputRef.current.value.trim();

      if (fbPageList != null) {
        const filteredAllSearchList = allPageList.filter((item, index, array) => {
          // Check if the name or ID matches the search term
          const isMatch =
            item.name.toLowerCase().includes(page.toLowerCase()) ||
            item.id.toString().toLowerCase().includes(page.toLowerCase());
          return isMatch;
        });

        setPageListDisplay(filteredAllSearchList);
        setSearchPageList(true);
      }
    };

    return (
      <Container>
        <DropdownButton
          columnSpacing="8px"
          minWidth="100%"
          backgroundColor={
            fbPageList == null || (fbPageList != null && facebookFormLoading.data.loading) ? "#f9f9f9" : "#fff"
          }
          onClick={fbPageList != null && facebookFormLoading.data.loading ? null : onDropdownButtonClicked}
          onSelected={handleOnSelected}
          disabled={fbValueDisabled || (!fbValueDisabled && facebookFormLoading.data.loading)}
          isOverflowing={isPageOverflowing}
        >
          <Column width="100%">
            <ItemContent isOverflowing={isPageOverflowing} type="text">
              <PageImage src={selectedPage.picture != null ? selectedPage.picture : fbPageImg} />
              <div className="page-name-area">
                <div className="page-name" ref={textPageRef}>
                  {selectedPage.name != null ? selectedPage.name : fbPageName}
                </div>
                <div className="page-id">{selectedPage.id != null ? selectedPage.id : fbPageId}</div>
              </div>
            </ItemContent>
          </Column>

          {fbPageList != null && (
            <Column wrap>
              <Icon name="icon-down-dir" size="14px" color="#555"></Icon>
            </Column>
          )}
        </DropdownButton>
        {fbPageList != null && (
          <>
            <ItemsContainer isVisible={isOptionsVisible} position={{ top: "112%" }} width="100%">
              <SearchContent>
                <StyledInput type="text" className="input-box" ref={inputRef} />
                <SearchArea onClick={() => searchPage()}>
                  <SearchIcon color="#555" />
                </SearchArea>
              </SearchContent>
              {pageListDisplay.length > 0 ? (
                <ScrollElement>
                  {pageListDisplay.map((item, index) => (
                    <DropdownItem key={index} item={item} index={index} onClick={onDropdownItemSelected}></DropdownItem>
                  ))}
                  {facebookPageListMore.result == 0 && <LoadMoreButtonContainer />}
                </ScrollElement>
              ) : (
                <StyledItem>
                  <div className="page-not-found">{t("__fbleads_fb_page_not_found")}</div>
                </StyledItem>
              )}
            </ItemsContainer>
            {isOptionsVisible && <Backdrop onClick={() => setOptionsVisible(false)} />}
          </>
        )}
      </Container>
    );
  };

  return <>{pageList()}</>;
};
export default FacebookPageList;

const Container = styled.div`
  position: relative;

  > *:first-child {
    cursor: pointer;
  }

  .custom-dropdown-button {
    display: flex;
    align-items: center;

    > * {
      min-width: 0;
    }
  }
`;

const DropdownButton = styled(Row).attrs((props) => ({
  minWidth: props.minWidth,
  opacity: props.disabled ? 0.75 : 1,
  backgroundColor: props.backgroundColor,
}))`
  border: 1px solid #ddd;
  padding: 4px 8px 4px 16px;
  border-radius: 6px;
  box-sizing: border-box;
  min-width: ${(props) => props.minWidth};
  opacity: ${(props) => props.opacity};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")} !important;
  background-color: ${(props) => props.backgroundColor};
  height: fit-content;
  align-items: center;

  &.no-border {
    border: none;
    background-color: transparent;
  }

  &.no-border .column > p {
    font-size: 1.2rem;
  }
`;

const ItemsContainer = styled.div.attrs((props) => ({
  display: props.isVisible ? "block" : "none",
  top: props.position ? props.position.top : "35px",
  right: props.position && props.position.right ? props.position.right : null,
  width: props.width,
}))`
  display: ${(props) => props.display};
  position: absolute;
  border: 1px solid #ddd;
  background-color: ${colors.backgroundSection};
  box-shadow: ${colors.sectionShadow} 0 10px 30px;
  min-width: 150px;
  border-radius: 8px;
  z-index: 999;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  width: ${(props) => props.width};
  padding: 16px 0;
  text-align: left;

  @media screen and (max-width: 834px) {
    box-shadow: none;
    width: calc(100% - 32px);
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 16px;
    text-align: center;
    max-height: calc(100vh - 150px);
    min-width: unset;

    p {
      font-size: 14px;
    }
  }
`;

const ScrollElement = styled.div`
  max-height: 210px;
  overflow: auto;
`;

const StyledItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;

  .page-not-found {
    font-size: 14px;
    color: #555;
    line-height: 1rem;
  }

  @media screen and (max-width: 834px) {
    padding: 16px;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 998;

  @media screen and (max-width: 834px) {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const ItemContent = styled.div.attrs((props) => ({
  wrap: props.isOverflowing ? "wrap" : "nowrap",
  wordBreak: props.isOverflowing ? "break-word" : "",
}))`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  color: #555;
  align-items: center;
  justify-content: flex-start;
  .page-data-area {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
  }
  .page-name-area {
    display: flex;
    flex-direction: column;
    .page-name {
      width: 300px;
      white-space: nowrap;
      line-height: 1.2rem;
      white-space: ${(props) => props.wrap};
      word-break: ${(props) => props.wordBreak};
    }
    .page-name-dropdown {
      white-space: wrap;
      line-height: 1rem;
      word-break: break-word;
      text-align: left;
    }
    .page-id {
      font-size: 12px;
      color: #aaa;
      width: 300px;
      white-space: nowrap;
      line-height: 1.2rem;
      white-space: ${(props) => props.wrap};
      word-break: ${(props) => props.wordBreak};
      margin-top: 5px;
    }
    .page-id-dropdown {
      font-size: 12px;
      color: #aaa;
      white-space: wrap;
      line-height: 1rem;
      word-break: break-word;
      margin-top: 5px;
      text-align: left;
    }
  }
`;

const PageImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 10px;
`;

const LoadMoreButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: #aaa;
  line-height: 1.5rem;

  .load-more-text {
    cursor: pointer;
  }

  .disabled {
    cursor: not-allowed;
  }
`;

const SearchIcon = styled(Search).attrs((props) => ({
  color: props.color,
}))`
  color: ${(props) => props.color};
  width: 16px;
  height: 16px;
`;

const SearchContent = styled.div`
  display: flex;
  flex-direction: row;
  line-height: 1.5rem;
  font-size: 14px;
  padding: 0px 10px 8px 16px;
`;

const StyledInput = styled.input`
  margin: 2px 0px;
  padding: 6px 6px 6px 10px;
  border: 1px solid #ddd;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  width 100%;
  font-size: 14px;

  &:only-child {
    margin-bottom: 0;
  }
`;

const SearchArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  border: 1px solid #ddd;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  border-left-width: 0;
  margin: 2px 0px;
  cursor: pointer;
`;

const CheckIcon = styled(Check2).attrs((props) => ({
  color: props.color,
}))`
  color: ${(props) => props.color};
  width: 20px;
  height: 20px;
`;
